/**
 * Mistify sticky Add to cart bar (snippets/mistify-sticky-atc.liquid)
 * - Shows on all screen sizes once the main Add to cart buttons have scrolled off the top of the screen.
 * - Its buttons click the MAIN Add to cart / Buy Now buttons, and its sizes click the MAIN size pills,
 *   so cart, drawer, COD checkout and variant changes all run through the existing code.
 * - Copies price, selected size and sold-out state from the main buy box after every variant change.
 */
(() => {
  const bar = document.querySelector('[data-mst-sticky]');
  if (!bar || bar.dataset.ready) return;
  bar.dataset.ready = 'true';

  const sectionId = bar.dataset.section;
  const addBtn = bar.querySelector('[data-mst-add]');
  const buyBtn = bar.querySelector('[data-mst-buy]');
  const priceEl = bar.querySelector('[data-mst-price]');
  const sizeBtns = Array.from(bar.querySelectorAll('[data-mst-size]'));

  const form = () => document.getElementById(`product-form-${sectionId}`);
  const mainAdd = () => document.getElementById(`ProductSubmitButton-${sectionId}`);
  const mainBuy = () => form()?.querySelector('.buy-now-button');
  const mainPrice = () => document.getElementById(`price-${sectionId}`);
  const firstOption = () => document.getElementById(`variant-selects-${sectionId}`)?.querySelector('fieldset');

  if (!mainBuy()) {
    buyBtn.remove();
    bar.classList.add('mst-sticky--single');
  }

  // ---------- show / hide ----------
  let pastButtons = false;
  let shown = false;
  const render = () => {
    const show = pastButtons;
    if (show === shown) return;
    shown = show;
    bar.classList.toggle('is-visible', show);
    bar.toggleAttribute('inert', !show);
    bar.setAttribute('aria-hidden', String(!show));
    document.body.classList.toggle('mst-sticky-on', show);
    if (show) document.documentElement.style.setProperty('--mst-sticky-h', `${bar.offsetHeight}px`);
  };

  const target = form()?.querySelector('.product-form__buttons') || mainAdd();
  if (!target) return;
  new IntersectionObserver(([entry]) => {
    pastButtons = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    render();
  }).observe(target);

  // ---------- copy state from the main buy box ----------
  const sync = () => {
    const add = mainAdd();
    const soldOut = !add || add.disabled;
    addBtn.disabled = soldOut;
    addBtn.textContent = soldOut ? window.variantStrings?.soldOut || 'Sold out' : addBtn.dataset.label;
    if (buyBtn.isConnected && buyBtn.getAttribute('aria-busy') !== 'true') buyBtn.disabled = soldOut;

    const box = mainPrice();
    if (box) {
      const onSale = box.querySelector('.price--on-sale');
      const now = box.querySelector(onSale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular');
      const was = onSale ? box.querySelector('.price__sale s') : null;
      if (now) {
        const strong = document.createElement('strong');
        strong.textContent = now.textContent.trim();
        priceEl.replaceChildren(strong);
        if (was) {
          const s = document.createElement('s');
          s.textContent = was.textContent.trim();
          priceEl.append(' ', s);
        }
      }
    }

    const checked = firstOption()?.querySelector('input[type="radio"]:checked');
    sizeBtns.forEach((btn) => btn.setAttribute('aria-pressed', String(!!checked && btn.dataset.mstSize === checked.value)));
    // show the picked size's own photo in the bar, when that size has one
    const picked = sizeBtns.find((btn) => btn.getAttribute('aria-pressed') === 'true');
    const thumb = bar.querySelector('.mst-sticky__thumb img');
    if (picked && picked.dataset.mstImg && thumb && thumb.dataset.src !== picked.dataset.mstImg) {
      thumb.removeAttribute('srcset');
      thumb.src = picked.dataset.mstImg;
      thumb.dataset.src = picked.dataset.mstImg;
    }
  };

  // ---------- actions ----------
  addBtn.addEventListener('click', () => mainAdd()?.click());

  buyBtn.addEventListener('click', () => {
    const main = mainBuy();
    if (!main || main.disabled) return;
    buyBtn.setAttribute('aria-busy', 'true');
    buyBtn.disabled = true;
    buyBtn.textContent = 'Processing…';
    main.click();
    // If buy-now.js shows an error instead of leaving the page, give the button back.
    setTimeout(() => {
      if (document.visibilityState === 'visible' && main.getAttribute('aria-busy') !== 'true') resetBuy();
    }, 4000);
  });

  const resetBuy = () => {
    if (!buyBtn.isConnected) return;
    buyBtn.removeAttribute('aria-busy');
    buyBtn.textContent = buyBtn.dataset.label;
    sync();
  };

  // ---------- keep the shopper where they are when a size is picked from the bar ----------
  // After a size change Dawn focuses the main size pill and scrolls to that size's photo,
  // which jumps the page back to the top. While a change started from the bar is running,
  // vertical window scrolls are ignored and the focus happens without scrolling.
  const nativeScrollTo = window.scrollTo.bind(window);
  const nativeFocus = HTMLElement.prototype.focus;
  let holding = false;
  let releaseTimer;
  const hold = () => {
    if (holding) return;
    holding = true;
    window.scrollTo = (...args) => {
      const opts = args[0];
      const vertical = typeof opts === 'object' ? opts && 'top' in opts : args.length > 1;
      if (!vertical) nativeScrollTo(...args);
    };
    HTMLElement.prototype.focus = function (options) {
      return nativeFocus.call(this, { ...(options || {}), preventScroll: true });
    };
    releaseTimer = setTimeout(release, 5000); // safety net
  };
  const release = () => {
    if (!holding) return;
    holding = false;
    clearTimeout(releaseTimer);
    window.scrollTo = nativeScrollTo;
    HTMLElement.prototype.focus = nativeFocus;
  };

  sizeBtns.forEach((btn) => {
    btn.addEventListener('click', () => {
      const input = Array.from(firstOption()?.querySelectorAll('input[type="radio"]') || []).find(
        (el) => el.value === btn.dataset.mstSize
      );
      if (!input || input.checked) return;
      sizeBtns.forEach((b) => b.setAttribute('aria-pressed', String(b === btn))); // instant feedback
      hold();
      input.click();
    });
  });

  // Dawn's constants.js declares PUB_SUB_EVENTS with `const`, so it is NOT on window.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      if (event?.data?.sectionId !== sectionId) return;
      sync();
      if (holding) setTimeout(release, 400); // after Dawn's delayed photo scroll and focus
    });
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) resetBuy();
  });

  sync();
})();
